import { useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Alert,
  Button as MuiButton
} from '@mui/material'
import { styled } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import DescriptionIcon from '@mui/icons-material/Description'
import UploadIcon from '@mui/icons-material/Upload'
import heic2any from 'heic2any'
import moment from 'moment'
import { Button } from 'components'
import { DocumentLoader } from 'pages/Customer/CustomerDetails/EditView/DragAndDrop/components'
import { addArnDocData, setArnDocFileWithData } from 'slices/arn_files'
import { setArnUploaded, setArnModal } from 'slices/securelinkslice'
import {
  generateUUID,
  getFormatedDateForCRM,
  collectDroppedFileNames
} from 'utils/helpers'
import {
  isAmexRefundCard,
  isArnSlotFilled,
  countFilledArnSlots,
  getMissingAmexArnPlaceholders
} from 'utils/arn'
import { setCurrentTab } from '@slices/customerSteps'
import { useCustomer } from 'api/useCustomer'
import { setCustomer } from 'slices/customer'
import { api, getErrorAttributes } from 'lib/axios'
import { getUserId } from 'lib/auth/tokenStorage'

const UploadArnDocModal = ({
  open,
  onClose,
  error,
  setError,
  loading,
  setLoading,
  setDisallowedImagesModal,
  setDuplicateImagesModal,
  setDuplicateImagesFiles,
  setDisAllowedFiles,
  setFilesArr,
  setArnUploadConfirmation,
  preventNavigation = false,
  autoUpdateStatus = false,
  onContinue = null
}) => {
  const isMounted = useRef(true)
  const inputRef = useRef(null)
  const fileInputRef = useRef()
  const isPastingRef = useRef(false)
  const handleCopyPasteArnRef = useRef(null)
  const loadingRef = useRef(loading)
  const agentId = getUserId()

  const dispatch = useDispatch()
  const reduxState = useSelector(state => state)
  const { securelink, customer, customer_files, tripAir, tripSea } = reduxState
  const paymentCards = useSelector(state => state?.paymentCards?.paymentCards)
  const arnFiles = useSelector(state =>
    state?.arn_files?.arn?.length > 0 ? state.arn_files.arn : []
  )
  const isAmexRefund = isAmexRefundCard(paymentCards)
  const pendingSlotIndexRef = useRef(0)
  const { handleSetStatusChange } = useCustomer()

  useEffect(() => {
    isMounted.current = true

    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (open && inputRef.current) {

      isPastingRef.current = false
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus()
        }
      }, 100)
    }
  }, [open])

  useEffect(() => {
    if (!open || !isAmexRefund) return
    const placeholders = getMissingAmexArnPlaceholders(arnFiles)
    placeholders.forEach(placeholder => {
      dispatch(addArnDocData(placeholder))
    })
  }, [open, isAmexRefund, arnFiles.length, dispatch])

  const getArnSlotDescription = slotIndex => {
    if (!isAmexRefund) return null
    if (slotIndex !== 0 && slotIndex !== 1) return null
    return `uploaded ARN ${slotIndex + 1}`
  }

  const trackArnActivity = async (slotIndex = null) => {
    try {
      const now = moment()
      const utcMoment = now.utc()
      const formattedTime = utcMoment.format('YYYY-MM-DD HH:mm:ss')
      const description = getArnSlotDescription(slotIndex)

      const body = {
        data: {
          type: 'gb_action_tracking_logs',
          attributes: {
            affiliate_member_id: agentId,
            action: 'upload_arn',
            action_time: formattedTime,
            parent_id: customer.id,
            ...(description ? { description } : {})
          }
        }
      }

      await api.post('/Api/V8/module', body)
    } catch (error) {
      console.error('Error tracking ARN activity:', error)
    }
  }

  const updateCustomerStatusToPaid = async () => {
    if (!autoUpdateStatus) {
      return
    }

    if (!customer?.id) {
      console.warn('Cannot update status: Customer ID is missing')
      return
    }

    if (customer?.status_c === 'paid' || customer?.status_c === 'Paid') {
      return
    }

    try {
      await handleSetStatusChange({
        contact_id: customer.id,
        agent_id: agentId,
        status_c: 'paid'
      })
      dispatch(setCustomer({ status_c: 'paid' }))
    } catch (error) {
      console.error('Error updating customer status to Paid:', error)
    }
  }

  const finishArnUploadIfReady = (filledAfterCount) => {
    const requiredCount = isAmexRefund ? 2 : 1
    if (filledAfterCount < requiredCount) {
      return false
    }
    dispatch(setArnUploaded({ arn: true }))
    dispatch(setArnModal({ haveArn: false }))
    if (!preventNavigation) {
      dispatch(setCurrentTab(3))
    }
    if (typeof setArnUploadConfirmation === 'function') {
      setArnUploadConfirmation(true)
    }
    return true
  }

  const saveArnData = async (obj, slotIndex = 0) => {
    if (
      obj?.file_obj &&
      obj?.file_obj?.document_revision_id &&
      obj?.file_obj?.document_revision_id !== ''
    ) {
      const arnFileStructure = {
        id: obj?.file_obj?.id || '',
        name: obj?.file_obj?.name || '',
        document_revision_id: obj?.file_obj?.document_revision_id || '',
        file_obj: {
          ...obj?.file_obj,
          gb_doc_type: 'arn'
        },
        gb_doc_type: 'arn',
        rotate_angle_c: obj?.file_obj?.rotate_angle_c || null,
        date_entered: obj?.file_obj?.date_entered || new Date().toISOString(),
        date_modified: obj?.file_obj?.date_modified || new Date().toISOString()
      }
      if (arnFiles[slotIndex]) {
        dispatch(
          setArnDocFileWithData({
            no: slotIndex,
            file: arnFileStructure
          })
        )
      } else {
        dispatch(
          addArnDocData({
            gb_doc_type: 'arn',
            files: [arnFileStructure]
          })
        )
      }
    }
    const attributes = Object.assign({}, tripAir.attributes)
    attributes.date_of_visit = getFormatedDateForCRM(attributes.date_of_visit)
    attributes.boarding_date_of_departure = getFormatedDateForCRM(
      attributes.boarding_date_of_departure
    )

    delete attributes.ocr
    delete attributes.verify
    delete attributes.itinerary_clicked
    delete attributes?.arn_clicked

    let files_arr = {}

    let id = ''
    if (obj?.file_obj?.id) {
      id = obj?.file_obj?.id
    }

    files_arr[0] = { arn: id }

    let trip_id = ''
    if (tripAir.attributes.id) {
      trip_id = tripAir.attributes.id
    } else if (tripSea.id) {
      trip_id = tripSea.id
    }

    let air_sea_type = ''
    if (tripAir.attributes.id && tripAir.attributes.air_sea_type) {
      air_sea_type = tripAir.attributes.air_sea_type
    } else if (tripSea.id && tripSea.air_sea_type) {
      air_sea_type = tripSea.air_sea_type
    }

    const apiCallData = {
      air_sea_type,
      date_of_visit: attributes.date_of_visit,
      entry_stamp: attributes.entry_stamp,
      boarding_verified: attributes.boarding_verified,
      send_secure_link: attributes.send_secure_link,
      boarding_first_name: attributes.boarding_first_name,
      boarding_last_name: attributes.boarding_last_name,
      boarding_airline_name: attributes.boarding_airline_name,
      boarding_flight_number: attributes.boarding_flight_number,
      boarding_exit_point: attributes.boarding_exit_point,
      boarding_date_of_departure: attributes.boarding_date_of_departure,
      id: trip_id
    }

    const response = await api.post('/Api/V8/custom/savedata/save-trip-data', {
      data: {
        basic: apiCallData,
        ocr: tripAir.attributes.ocr,
        verification: tripAir.attributes.verify,
        contact_id: customer.id,
        files: files_arr
      }
    })
  }

  const heifImageConverter = async heifImg => {
    let imageFile = heifImg
    try {
      const blob = await heic2any({
        blob: heifImg,
        toType: 'image/jpg'
      })
      imageFile = new File([blob], heifImg.name.replace(/\.[^/.]+$/, '.jpg'), {
        type: 'image/jpg'
      })
      return imageFile
    } catch (error) {
      console.error('Conversion error:', error)
      // Return the original file instead of undefined
      return heifImg
    }
  }

  const isSpreadsheetExtension = file => {
    const name = (file?.name || '').toLowerCase()
    return ['.xlsx', '.xls'].some(ext => name.endsWith(ext))
  }

  const filterFiles = async filesData => {
    const allowedTypes = [
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/webp',
      'image/heif',
      'image/heic',
      'application/pdf'
    ]

    const allowedFiles = []
    const nonAllowedFiles = []

    for (const file of filesData) {
      if (isSpreadsheetExtension(file)) {
        nonAllowedFiles.push(file)
        continue
      }

      if (allowedTypes.includes(file.type)) {
        if (file.type === 'image/heif' || file.type === 'image/heic') {
          const convertedFile = await heifImageConverter(file)
          // Only push if conversion was successful
          if (convertedFile) {
            allowedFiles.push(convertedFile)
          } else {
            nonAllowedFiles.push(file)
          }
        } else {
          allowedFiles.push(file)
        }
      } else {
        nonAllowedFiles.push(file)
      }
    }

    return {
      allowedFiles,
      nonAllowedFiles
    }
  }

  const prepareFilesArray = (array = []) => {
    const normalizeName = n =>
      typeof n === 'string' ? n.trim().toLowerCase() : ''
    const stripExt = n => n.replace(/\.[^.\\/]+$/, '')

    const collectNames = list => {
      const full = new Set()
      const base = new Set()
      for (const f of list || []) {
        const raw =
          f?.file_obj?.name ?? f?.file_obj?.file_name ?? f?.fileName ?? ''
        const norm = normalizeName(raw)
        if (!norm) continue
        full.add(norm)
        const b = stripExt(norm)
        if (b) base.add(b)
      }
      return { full, base }
    }

    const existing = collectNames(customer_files?.files)

    const droppedNamesRaw = (() => {
      try {
        return collectDroppedFileNames(reduxState)
      } catch (_) {
        return new Set()
      }
    })()
    const droppedFull = new Set()
    const droppedBase = new Set()
    droppedNamesRaw.forEach(raw => {
      const norm = normalizeName(raw)
      if (!norm) return
      droppedFull.add(norm)
      const b = stripExt(norm)
      if (b) droppedBase.add(b)
    })

    const allExistingFull = new Set([...existing.full, ...droppedFull])
    const allExistingBase = new Set([...existing.base, ...droppedBase])

    const seenInBatch = new Set()
    const seenInBatchBase = new Set()

    const duplicates = []
    const validFiles = []

    array.forEach((item, idx) => {
      const name = item?.name ?? ''
      const normalized = normalizeName(name)
      const normalizedBase = stripExt(normalized)

      const obj = {
        file_no: idx,
        file: item,
        id: generateUUID(),
        fileName: name || '',
        status: 'Waiting for upload...',
        progress: 0,
        isUploaded: true,
        file_obj: {
          id: '',
          document_revision_id: '',
          gb_doc_type: null,
          name: '',
          rotate_angle_c: null
        }
      }

      const isDuplicate =
        !normalized ||
        allExistingFull.has(normalized) ||
        seenInBatch.has(normalized) ||
        (!!normalizedBase &&
          (allExistingBase.has(normalizedBase) ||
            seenInBatchBase.has(normalizedBase)))

      if (isDuplicate) {
        duplicates.push(obj)
      } else {
        validFiles.push(obj)
        seenInBatch.add(normalized)
        if (normalizedBase) seenInBatchBase.add(normalizedBase)
      }
    })

    if (typeof setDuplicateImagesFiles === 'function') {
      setDuplicateImagesFiles(duplicates)
    }

    const newArr = validFiles.map((item, idx) => {
      return {
        file_no: idx,
        ...item
      }
    })

    if (typeof setFilesArr === 'function') {
      setFilesArr(newArr)
    }

    return { validFiles, duplicates }
  }

  const getToken = async () => {
    const token = localStorage.getItem('access_token')
    return token
  }

  const uploadFile = async data => {
    // Validate input data structure
    if (!data || typeof data !== 'object') {
      console.error('uploadFile: Invalid data structure provided', data)
      if (isMounted.current) setError('Invalid file data structure')
      return
    }

    const { file, id } = data

    // Validate required properties
    if (!file) {
      console.error('uploadFile: Missing file property', data)
      if (isMounted.current) setError('Missing file data')
      return
    }

    if (!id) {
      console.error('uploadFile: Missing id property', data)
      if (isMounted.current) setError('Missing file identifier')
      return
    }

    let errStatus = ''
    const formData = new FormData()
    formData.append('images[]', file)
    formData.append('contact_id', customer.id)
    formData.append('agent_id', agentId)

    try {
      const response = await api.post(
        '/Api/V8/custom/customer/process-all-files',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 600000
        }
      )

      if (response.status === 200) {
        const fileData = response.data
        if (fileData?.length > 0) {
          let updatedFile

          // Check if setFilesArr is a function before calling it
          if (typeof setFilesArr === 'function') {
            // Create updatedFile first, outside the callback
            updatedFile = {
              id,
              file_obj: { ...fileData[0] },
              status: 'Uploaded'
            }

            setFilesArr(prevFilesArr => {
              const updatedFiles = prevFilesArr.map(fileItem => {
                if (fileItem.id === id) {
                  // Return the pre-created updatedFile object
                  return updatedFile
                }
                return fileItem
              })
              return updatedFiles
            })
          }

          if (updatedFile && isMounted.current) {
            await saveArnData(updatedFile, pendingSlotIndexRef.current || 0)
          }
        } else {
          if (isMounted.current)
            setError('Unable to upload ARN file, please try again.')
        }
      } else {
        if (isMounted.current)
          setError('Unable to upload ARN file, please try again.')
      }
    } catch (error) {
      if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
        errStatus = 'Network error. Please check your connection and retry.'
      } else if (error.message?.includes('ERR_CONNECTION_REFUSED')) {
        errStatus = 'Server unavailable, Connection refused'
      } else {
        const { message } = getErrorAttributes(error)
        errStatus =
          message !== 'Unknown Error'
            ? message
            : 'Unable to upload ARN file, please try again.'
      }
      if (isMounted.current) setError(errStatus)
    }
  }

  const handleFileChange = async e => {
    if (!isMounted.current) return

    try {
      if (isMounted.current) setError('')
      if (isMounted.current) setLoading(true)

      const selectedFiles = Array.from(e.target.files)
      const filesForSlot = isAmexRefund ? selectedFiles.slice(0, 1) : selectedFiles

      // Add validation for selected files
      if (!filesForSlot || filesForSlot.length === 0) {
        if (isMounted.current) {
          setError('No files selected.')
          setLoading(false)
        }
        return
      }

      const { allowedFiles, nonAllowedFiles } = await filterFiles(filesForSlot)

      let duplicatesArr = []
      if (allowedFiles?.length > 0) {
        const { validFiles, duplicates } = prepareFilesArray(allowedFiles)
        duplicatesArr = [...duplicates]

        try {
          if (validFiles?.length > 0) {
            await getToken()

            for (let index = 0; index < validFiles.length; index++) {
              await uploadFile(validFiles[index])
            }
          }
        } catch (error) {
          console.error('Upload error:', error)
          if (isMounted.current)
            setError('Unable to upload ARN file, please try again.')
        } finally {
          if (isMounted.current && validFiles?.length > 0) {
            trackArnActivity(pendingSlotIndexRef.current)
            const filledAfter = countFilledArnSlots(arnFiles) + validFiles.length
            const completed = finishArnUploadIfReady(filledAfter)
            if (completed) {
              await updateCustomerStatusToPaid()
            }
          }
          if (isMounted.current) setLoading(false)
        }
      }

      if (nonAllowedFiles?.length > 0 && isMounted.current) {
        setLoading(false)
        if (typeof setDisallowedImagesModal === 'function') {
          setDisallowedImagesModal(true)
        }
        if (typeof setDisAllowedFiles === 'function') {
          setDisAllowedFiles(nonAllowedFiles)
        }
      }

      if (duplicatesArr?.length > 0 && isMounted.current) {
        setLoading(false)
        if (typeof setDuplicateImagesModal === 'function') {
          setDuplicateImagesModal(true)
        }
      }
    } catch (error) {
      console.error('File change error:', error)
      if (isMounted.current) {
        setError(
          `An error occurred during file upload: ${error.message || 'Unknown error'
          }`
        )
      }
    } finally {
      if (e?.target) e.target.value = ''
      if (isMounted.current) setLoading(false)
    }
  }

  const openFileDialog = (slotIndex = 0) => {
    pendingSlotIndexRef.current = slotIndex
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const uploadArnCopyPasteFile = async data => {
    const { file } = data
    let errStatus = ''
    const formData = new FormData()
    formData.append('images[]', file)
    formData.append('contact_id', customer.id)
    formData.append('agent_id', agentId)

    try {
      const response = await api.post(
        '/Api/V8/custom/customer/process-all-files',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          timeout: 600000
        }
      )
      if (response.status === 200) {
        const fileData = response.data
        if (fileData?.length > 0) {
          let updatedFile = {
            file_obj: { ...fileData[0] }
          }

          if (updatedFile && isMounted.current) {
            await saveArnData(updatedFile, pendingSlotIndexRef.current || 0)
          }
          return true
        } else {
          if (isMounted.current)
            setError('Unable to upload ARN file, please try again.')
          return false
        }
      }
    } catch (error) {
      if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
        errStatus = 'Network error. Please check your connection and retry.'
      } else if (error.message?.includes('ERR_CONNECTION_REFUSED')) {
        errStatus = 'Server unavailable, Connection refused'
      } else {
        const { message } = getErrorAttributes(error)
        errStatus =
          message !== 'Unknown Error'
            ? message
            : 'Unable to upload ARN file, please try again.'
      }
      if (isMounted.current) setError(errStatus)
      return false
    }
  }

  const getFirstEmptyArnSlot = () => {
    if (isAmexRefund) {
      if (!isArnSlotFilled(arnFiles[0])) return 0
      if (!isArnSlotFilled(arnFiles[1])) return 1
      return 0
    }
    return 0
  }

  const handleCopyPasteArn = async file => {
    if (!isMounted.current) {
      return false
    }

    if (!file) {
      return false
    }

    if (isAmexRefund) {
      if (isArnSlotFilled(arnFiles[pendingSlotIndexRef.current])) {
        pendingSlotIndexRef.current = getFirstEmptyArnSlot()
      }
      if (isArnSlotFilled(arnFiles[pendingSlotIndexRef.current])) {
        return false
      }
    }

    if (isSpreadsheetExtension(file)) {
      setError('')
      if (typeof setDisallowedImagesModal === 'function') {
        setDisallowedImagesModal(true)
      }
      if (typeof setDisAllowedFiles === 'function') {
        setDisAllowedFiles([file])
      }
      return false
    }

    if (!file.type) {
      setError('Invalid pasted content. Please paste an image.')
      return false
    }

    setLoading(true)
    setError('')

    try {
      const { allowedFiles, nonAllowedFiles } = await filterFiles([file])
      if (nonAllowedFiles?.length > 0 || !allowedFiles?.length) {
        setError('')
        if (typeof setDisallowedImagesModal === 'function') {
          setDisallowedImagesModal(true)
        }
        if (typeof setDisAllowedFiles === 'function') {
          setDisAllowedFiles(
            nonAllowedFiles?.length ? nonAllowedFiles : [file]
          )
        }
        return false
      }

      const validFile = allowedFiles[0]
      const pastedFileName = validFile.name || ''

      const normalize = n =>
        typeof n === 'string' ? n.trim().toLowerCase() : ''
      const stripExt = n => n.replace(/\.[^.\\/]+$/, '')

      const pastedNorm = normalize(pastedFileName)
      const pastedBase = stripExt(pastedNorm)

      const collectFull = new Set()
      const collectBase = new Set()
        ; (customer_files?.files || []).forEach(f => {
          const raw =
            f?.file_obj?.name ?? f?.file_obj?.file_name ?? f?.fileName ?? ''
          const norm = normalize(raw)
          if (!norm) return
          collectFull.add(norm)
          const base = stripExt(norm)
          if (base) collectBase.add(base)
        })

      try {
        collectDroppedFileNames(reduxState).forEach(raw => {
          const norm = normalize(raw)
          if (!norm) return
          collectFull.add(norm)
          const base = stripExt(norm)
          if (base) collectBase.add(base)
        })
      } catch (_) {
        // fall back silently to customer_files-only set
      }

      const isDuplicatePaste =
        (!!pastedNorm && collectFull.has(pastedNorm)) ||
        (!!pastedBase && collectBase.has(pastedBase))

      if (isDuplicatePaste) {
        setError('')
        if (typeof setDuplicateImagesModal === 'function') {
          setDuplicateImagesModal(true)
        }
        if (typeof setDuplicateImagesFiles === 'function') {
          setDuplicateImagesFiles([
            {
              file_no: 0,
              file: validFile,
              id: generateUUID(),
              fileName: pastedFileName,
              status: 'Waiting for upload...',
              progress: 0,
              isUploaded: true,
              file_obj: {
                id: '',
                document_revision_id: '',
                gb_doc_type: null,
                name: '',
                rotate_angle_c: null
              }
            }
          ])
        }
        return false
      }

      await getToken()

      const fileExtension = validFile.type.split('/')[1] || 'png'
      const hasOriginalName =
        !!validFile.name && validFile.name.toLowerCase() !== 'image.png'
      const updatedFileName = hasOriginalName
        ? validFile.name
        : `ARN_DOC_${Math.floor(10000 + Math.random() * 90000)}.${fileExtension}`

      const updatedFile = new File([validFile], updatedFileName, {
        type: validFile.type,
        lastModified: validFile.lastModified
      })

      const isSuccess = await uploadArnCopyPasteFile({
        id: generateUUID(),
        file: updatedFile
      })

      if (!isSuccess) {
        return false
      }

      trackArnActivity(pendingSlotIndexRef.current)
      const filledAfter =
        countFilledArnSlots(arnFiles) +
        (isArnSlotFilled(arnFiles[pendingSlotIndexRef.current]) ? 0 : 1)
      const completed = finishArnUploadIfReady(filledAfter)
      if (completed) {
        await updateCustomerStatusToPaid()
      }
      return true
    } catch (error) {
      if (isMounted.current)
        setError('Unable to upload ARN file, please try again.')
      return false
    } finally {
      if (isMounted.current) setLoading(false)
    }
  }

  const handlePasteArn = async (event, slotIndex = null) => {
    event.preventDefault()

    if (!isMounted.current) return

    if (isPastingRef.current || loading) {
      return
    }

    const targetSlot =
      slotIndex !== null && slotIndex !== undefined
        ? slotIndex
        : !isArnSlotFilled(arnFiles[pendingSlotIndexRef.current])
          ? pendingSlotIndexRef.current
          : getFirstEmptyArnSlot()
    if (isAmexRefund && isArnSlotFilled(arnFiles[targetSlot])) {
      return
    }
    pendingSlotIndexRef.current = targetSlot

    const clipboardItems = event.clipboardData?.items || []
    const clipboardFiles = event.clipboardData?.files || []

    let pastedFile = null
    for (const item of clipboardItems) {
      if (item.kind === 'file') {
        const f = item.getAsFile()
        if (f) {
          pastedFile = f
          break
        }
      }
    }
    if (!pastedFile && clipboardFiles.length > 0) {
      pastedFile = clipboardFiles[0]
    }

    if (!pastedFile) return
    isPastingRef.current = true
    try {
      await handleCopyPasteArn(pastedFile)
    } finally {
      isPastingRef.current = false
    }
  }

  useEffect(() => {
    handleCopyPasteArnRef.current = handleCopyPasteArn
    loadingRef.current = loading
  })


  useEffect(() => {
    if (!open) return

    const readClipboardFile = async () => {
      if (!isMounted.current || isPastingRef.current || loadingRef.current) {
        return
      }

      let pastedFile = null
      try {
        const items = await navigator.clipboard.read()
        for (const item of items) {
          for (const type of item.types) {
            if (type.startsWith('image/') || type === 'application/pdf') {
              const blob = await item.getType(type)
              const ext = (type.split('/')[1] || 'png').toLowerCase()
              pastedFile = new File([blob], `pasted_arn.${ext}`, { type })
              break
            }
          }
          if (pastedFile) break
        }
      } catch (err) {
        console.warn('Async clipboard read failed:', err)
        return
      }

      if (!pastedFile) return

      isPastingRef.current = true
      try {
        const handler = handleCopyPasteArnRef.current
        if (!handler) return
        await handler(pastedFile)
      } finally {
        isPastingRef.current = false
      }
    }

    const handleKeyDown = event => {
      const isPasteShortcut =
        (event.ctrlKey || event.metaKey) &&
        (event.code === 'KeyV' || event.key === 'v' || event.key === 'V')
      if (!isPasteShortcut) return

      const activeEl = document.activeElement
      const isPasteTarget =
        activeEl &&
        (activeEl.tagName === 'INPUT' ||
          activeEl.tagName === 'TEXTAREA' ||
          activeEl.isContentEditable)

      if (isPasteTarget) {
        return
      }

      event.preventDefault()
      readClipboardFile()
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open])

  const focusInput = () => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }

  const handleClose = () => {
    if (!loading) {
      onClose()
      setError('')
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={isAmexRefund ? 'md' : 'sm'}
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            minHeight: isAmexRefund ? '420px' : '400px',
            maxWidth: isAmexRefund ? '760px' : undefined,
            position: 'relative'
          }
        }
      }}
    >
      <div
        ref={inputRef}
        contentEditable
        suppressContentEditableWarning
        role="application"
        aria-label="ARN upload area. Paste an image with Ctrl+V or right-click and choose Paste."
        style={{
          position: 'relative',
          outline: 'none',
          caretColor: 'transparent',
          cursor: 'default'
        }}
        onPaste={handlePasteArn}
        onBeforeInput={e => e.preventDefault()}
        onKeyDown={e => {
          const isPasteShortcut =
            (e.ctrlKey || e.metaKey) &&
            (e.code === 'KeyV' || e.key === 'v' || e.key === 'V')
          const isAllowedKey =
            ['Tab', 'Escape', 'Shift', 'Control', 'Meta', 'Alt'].includes(
              e.key
            )
          if (!isPasteShortcut && !isAllowedKey) {
            e.preventDefault()
          }
        }}
        onCut={e => e.preventDefault()}
        onCopy={e => e.preventDefault()}
        onDragStart={e => e.preventDefault()}
        onDrop={e => e.preventDefault()}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            color: '#d32f2f',
            fontWeight: 'bold',
            fontSize: '1.2rem',
            borderBottom: '1px solid #e0e0e0',
            pb: 2,
            mb: 1
          }}
        >
          MISSING ARN!
          {!loading && (
            <IconButton
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: '#666'
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>

        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4,
            px: 3,
            cursor: 'default'
          }}
          onClick={focusInput}
        >
          {loading ? (
            <DocumentLoader
              thickness={4}
              text="Uploading ARN Files..."
              mt={5}
            />
          ) : (
            <>
              {error && error !== '' && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                  {error}
                </Alert>
              )}

              {isAmexRefund ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'stretch',
                    gap: 2,
                    width: '100%'
                  }}
                >
                  {[0, 1].map(slotIndex => {
                    const slotFilled = isArnSlotFilled(arnFiles[slotIndex])
                    return (
                      <DashedDropArea
                        key={`arn-slot-${slotIndex}`}
                        onClick={() => {
                          pendingSlotIndexRef.current = slotIndex
                          focusInput()
                        }}
                        onPaste={event => handlePasteArn(event, slotIndex)}
                        sx={{
                          flex: '1 1 0',
                          width: 'auto',
                          minWidth: 0,
                          marginTop: 0
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700, mb: 1, color: '#333' }}
                        >
                          ARN {slotIndex + 1}
                        </Typography>
                        {slotFilled ? (
                          <Typography
                            variant="body2"
                            sx={{ color: '#2e7d32', fontWeight: 600 }}
                          >
                            Uploaded
                          </Typography>
                        ) : (
                          <>
                            <CloudWrapper>
                              <SideDocIcon style={{ top: -6, left: -8 }}>
                                <DescriptionIcon
                                  sx={{ fontSize: '1.6rem', color: '#d4a853' }}
                                />
                              </SideDocIcon>
                              <CenterCloud>
                                <UploadIcon
                                  sx={{ fontSize: '2.4rem', color: '#d4a853' }}
                                />
                              </CenterCloud>
                              <SideDocIcon style={{ top: -6, right: -8 }}>
                                <DescriptionIcon
                                  sx={{ fontSize: '1.6rem', color: '#d4a853' }}
                                />
                              </SideDocIcon>
                            </CloudWrapper>
                            <Typography
                              variant="body2"
                              sx={{
                                textAlign: 'center',
                                color: '#666',
                                mt: 2,
                                mb: 2,
                                lineHeight: 1.5
                              }}
                            >
                              Upload or paste ARN {slotIndex + 1}
                              <br />
                              to Merge the PDF
                            </Typography>
                            <Button
                              text={`UPLOAD ARN ${slotIndex + 1}`}
                              onClick={event => {
                                event.stopPropagation()
                                openFileDialog(slotIndex)
                              }}
                              background_color="#ffc107"
                              color="black"
                              padding="8px 16px"
                              font_size="14px"
                              width="auto"
                              font_weight="bold"
                              border_radius="6px"
                            />
                          </>
                        )}
                      </DashedDropArea>
                    )
                  })}
                </Box>
              ) : (
                <DashedDropArea onClick={focusInput}>
                  <CloudWrapper>
                    <SideDocIcon style={{ top: -6, left: -8 }}>
                      <DescriptionIcon
                        sx={{ fontSize: '1.6rem', color: '#d4a853' }}
                      />
                    </SideDocIcon>
                    <CenterCloud>
                      <UploadIcon sx={{ fontSize: '2.4rem', color: '#d4a853' }} />
                    </CenterCloud>
                    <SideDocIcon style={{ top: -6, right: -8 }}>
                      <DescriptionIcon
                        sx={{ fontSize: '1.6rem', color: '#d4a853' }}
                      />
                    </SideDocIcon>
                  </CloudWrapper>

                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: 'center',
                      color: '#666',
                      mt: 2,
                      mb: 2,
                      lineHeight: 1.5
                    }}
                  >
                    ARN documents must be uploaded in
                    <br />
                    order to Merge the PDF
                  </Typography>

                  <Button
                    text={'UPLOAD ARN'}
                    onClick={() => openFileDialog(0)}
                    background_color="#ffc107"
                    color="black"
                    padding="8px 16px"
                    font_size="14px"
                    width="auto"
                    font_weight="bold"
                    border_radius="6px"
                  />
                </DashedDropArea>
              )}

              <input
                type="file"
                id="fileInput"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
                multiple={!isAmexRefund}
                accept="image/*,application/pdf"
              />
            </>
          )}
        </DialogContent>

        {/* Continue and Close buttons - shown when onContinue prop is provided */}
        {!loading && onContinue && !isAmexRefund && (
          <DialogActions
            sx={{
              p: 2,
              pt: 1,
              display: 'flex',
              justifyContent: 'space-between',
              borderTop: '1px solid #e0e0e0'
            }}
          >
            <MuiButton
              onClick={async () => {
                if (onContinue && typeof onContinue === 'function') {
                  await onContinue()
                }
              }}
              variant="outlined"
              sx={{
                color: "#e53935",
                borderColor: "#e53935",
                borderRadius: "4px",
                backgroundColor: "#F4E9D8",
                textTransform: "lowercase",
                fontWeight: 600,

              }}
            >
              Continue
            </MuiButton>
            <MuiButton
              onClick={handleClose}
              variant="outlined"
              sx={{
                color: "#e53935",
                borderColor: "#e53935",
                borderRadius: "4px",
                textTransform: "lowercase",
                fontWeight: 600,
                backgroundColor: "#F4E9D8",
              }}
            >
              Close
            </MuiButton>
          </DialogActions>
        )}
      </div>
    </Dialog>
  )
}



// New styled components for dashed container layout
const DashedDropArea = styled(Box)(({ theme }) => ({
  width: '100%',
  border: '2px dashed #e0e0e0',
  flexDirection: 'column',
  marginTop: '8px',
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2)
}))

const CloudWrapper = styled('div')(({ theme }) => ({
  position: 'relative',
  width: 96,
  height: 96,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}))

const CenterCloud = styled('div')(({ theme }) => ({
  width: 72,
  height: 72,
  borderRadius: '50%',
  backgroundColor: '#f7f2e6',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: 'inset 0 0 0 2px #f0e2bb'
}))

const SideDocIcon = styled('div')(({ theme }) => ({
  position: 'absolute'
}))

export default UploadArnDocModal
